import { Component, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatIconModule } from '@angular/material/icon';

import { FormDialogShellComponent } from '@shared/components/form-dialog/form-dialog-shell.component';
import { AutoSelectComponent } from '@app/shared/components/auto-select/auto-select.component';
import { CustomMessageService } from '@shared/components/custom-message/custom-message.service';
import { PlacesService } from '@shared/services/entities/places.service';
import { DoctorsService } from '@shared/services/entities/doctors.service';
import { GaFormsService } from '@shared/services/entities/ga-forms.service';
import { UserScopeService } from '@app/shared/services/user-scope.service';
import { GaPdfService } from './ga-pdf.service';
import { buildInventoryControlDoc, buildPreferenceDoc } from './ga-bitacoras';

export type GaFormsMode = 'preference' | 'inventory';
/** scopePlaces: hospitales del usuario; si no se pasa, el diálogo lo consulta (el API recorta igual). */
export interface GaFormsDialogData { mode: GaFormsMode; scopePlaces?: string[] | null; place?: string | null; }

const SHIFT_PAIRS = [
  { value: 'M,V', label: 'Matutino (7–15) y vespertino (15–22)' },
  { value: 'V,N', label: 'Vespertino (15–22) y nocturno (22–7)' },
  { value: 'N', label: 'Solo nocturno (22–7)' },
];

/**
 * Pide los datos para imprimir un formato GA y lo descarga:
 *  · Bitácora de preferencia médica (médico y, opcional, hospital).
 *  · Control de inventario (hospital, fecha y turnos).
 * El cálculo y el recorte por alcance los hace el API (/gaForms/*).
 */
@Component({
  selector: 'app-ga-forms-dialog',
  standalone: true,
  imports: [FormsModule, MatDialogModule, MatFormFieldModule, MatInputModule, MatSelectModule, MatIconModule,
    FormDialogShellComponent, AutoSelectComponent],
  styles: [`
    .form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px 16px; padding-top: 4px;
      @media (max-width: 640px) { grid-template-columns: 1fr; } }
    .full-col { grid-column: 1 / -1; }
    .hint-box { grid-column: 1 / -1; display: flex; gap: 8px; align-items: flex-start; background: #EEF3FF;
      border-radius: 8px; padding: 8px 10px; font-size: 13px; line-height: 1.45; color: #3C3C3C;
      mat-icon { font-size: 18px; width: 18px; height: 18px; color: #1010EB; flex-shrink: 0; } }
  `],
  template: `
<app-form-dialog-shell [icon]="isPreference ? 'medical_information' : 'inventory'"
  [heading]="isPreference ? 'Bitácora de preferencia médica' : 'Control de inventario'"
  [saving]="saving()" [disabled]="!canSubmit" saveLabel="Descargar PDF" saveIcon="picture_as_pdf"
  formId="ga-forms-form" (cancelled)="ref.close(false)">
<form id="ga-forms-form" (ngSubmit)="submit()">
  <div class="form-grid">
    @if (isPreference) {
      <app-auto-select class="full-col" label="Hospital (opcional)" name="place" [(ngModel)]="place" (changed)="onPlace()"
        [options]="placeOptions" [labelFn]="placeLabel" [allowEmpty]="placeOptions.length > 1" emptyLabel="Todos mis hospitales"></app-auto-select>
      <app-auto-select class="full-col" label="Médico" name="doctor" [(ngModel)]="doctor" [options]="doctorOptions"></app-auto-select>
      <p class="hint-box"><mat-icon>info</mat-icon>
        <span>Se arma con los folios del médico: por cada cirugía entran los códigos usados en al menos la mitad de sus folios, con la cantidad más frecuente.</span></p>
    } @else {
      <app-auto-select class="full-col" label="Hospital" name="place" [(ngModel)]="place" [options]="placeOptions" [labelFn]="placeLabel"></app-auto-select>
      <mat-form-field appearance="outline">
        <mat-label>Fecha</mat-label>
        <input matInput type="date" name="date" [(ngModel)]="date" [max]="today" required>
      </mat-form-field>
      <mat-form-field appearance="outline">
        <mat-label>Turnos</mat-label>
        <mat-select name="shifts" [(ngModel)]="shifts">
          @for (s of shiftPairs; track s.value) { <mat-option [value]="s.value">{{ s.label }}</mat-option> }
        </mat-select>
      </mat-form-field>
      <p class="hint-box"><mat-icon>info</mat-icon>
        <span>Recibido y entregó son las piezas disponibles en el hospital al inicio y al final de cada turno; consumo, las piezas de los folios del turno; DLV, lo que entró. Un turno que no ha terminado sale sin "entregó".</span></p>
    }
  </div>
</form>
</app-form-dialog-shell>
  `,
})
export class GaFormsDialogComponent {
  readonly ref = inject(MatDialogRef<GaFormsDialogComponent>);
  readonly data = inject<GaFormsDialogData>(MAT_DIALOG_DATA);
  private readonly places = inject(PlacesService);
  private readonly doctors = inject(DoctorsService);
  private readonly gaForms = inject(GaFormsService);
  private readonly gaPdf = inject(GaPdfService);
  private readonly msg = inject(CustomMessageService);
  private readonly userScope = inject(UserScopeService);
  private scope: string[] | null = null;

  readonly isPreference = this.data.mode === 'preference';
  readonly shiftPairs = SHIFT_PAIRS;
  readonly today = localDate(new Date());
  readonly saving = signal(false);

  placeOptions: any[] = [];
  doctorOptions: any[] = [];
  private allDoctors: any[] = [];
  place: string | null = this.data.place ?? null;
  doctor: string | null = null;
  date = this.today;
  shifts = new Date().getHours() >= 22 || new Date().getHours() < 7 ? 'V,N' : 'M,V';

  readonly placeLabel = (p: any): string => (p ? (p.alias ? `${p.alias} — ${p.name ?? ''}` : (p.name ?? '')) : '');

  constructor() { void this.load(); }

  get canSubmit(): boolean {
    return this.isPreference ? !!this.doctor : !!(this.place && this.date && this.shifts);
  }

  private async load(): Promise<void> {
    const cond = { searchType: 'default', condition: { deleted: false } };
    try {
      const [p, d]: any[] = await Promise.all([
        this.places.search(cond),
        this.isPreference ? this.doctors.search(cond) : Promise.resolve(null),
      ]);
      const scope = this.scope = this.data.scopePlaces !== undefined
        ? this.data.scopePlaces
        : ((await this.userScope.load().catch(() => null))?.places ?? null);
      this.placeOptions = ((p?.data ?? []) as any[])
        .filter(x => x.active !== false && (!scope || scope.includes(String(x.id))))
        .sort((a, b) => this.placeLabel(a).localeCompare(this.placeLabel(b)));
      if (this.place && !this.placeOptions.some(x => x.id === this.place)) this.place = null;
      if (!this.place && this.placeOptions.length === 1) this.place = this.placeOptions[0].id;
      this.allDoctors = ((d?.data ?? []) as any[]).filter(x => x.active !== false)
        .sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
      this.onPlace();
    } catch {
      await this.msg.open({ title: 'Error', type: 'error', message: 'No se pudieron cargar los catálogos.', dismissBtn: true });
    }
  }

  onPlace(): void {
    if (!this.isPreference) return;
    const scope = this.scope;
    const placesOf = (d: any): string[] => (Array.isArray(d?.places) && d.places.length ? d.places : (d?.place ? [d.place] : [])).map(String);
    this.doctorOptions = this.allDoctors.filter(d => {
      const ps = placesOf(d);
      if (this.place) return ps.includes(String(this.place));
      return !scope || !ps.length || ps.some(x => scope.includes(x));
    });
    if (this.doctor && !this.doctorOptions.some(d => d.id === this.doctor)) this.doctor = null;
  }

  async submit(): Promise<void> {
    if (!this.canSubmit || this.saving()) return;
    this.saving.set(true);
    try {
      if (this.isPreference) {
        const data = await this.gaForms.doctorPreference(this.doctor!, this.place);
        if (!data.surgeries.length) {
          await this.msg.open({ title: 'Sin folios', type: 'warning', dismissBtn: true,
            message: 'Este médico no tiene folios con insumos capturados en tus hospitales.' });
          return;
        }
        this.gaPdf.download(buildPreferenceDoc(data), `Preferencia-${fileSafe(data.doctor)}`);
      } else {
        const data = await this.gaForms.inventoryControl(this.place!, this.date, this.shifts);
        this.gaPdf.download(buildInventoryControlDoc(data), `Control-inventario-${fileSafe(data.hospital)}-${this.date}`);
      }
      this.ref.close(true);
    } catch (err: any) {
      await this.msg.open({ title: 'Error', type: 'error', dismissBtn: true,
        message: err?.message || 'No se pudo generar el formato.' });
    } finally {
      this.saving.set(false);
    }
  }
}

function localDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fileSafe(text: string): string {
  return String(text ?? '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'formato';
}
